import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import { LANDSCAPE_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/landscapeBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { forestBiome } from '../biomeArchetypes';
import { FIRST_FIELD } from './firstField';
import { JOURNEY_DEPTH_BACKDROPS, JOURNEY_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/journeyDepthBackdrops';
export { TIDEWEND_OUTSKIRTS } from './tidewendOutskirts';

const drownedArt = Object.values(LANDSCAPE_BACKDROPS.drowned);
const drownedHeights = [560, 380, 240];
const drownedPlanes = FIRST_FIELD.layers.filter(layer => layer.kind === 'parallax')
  .map((layer, index) => ({ ...layer, height: drownedHeights[index] ?? layer.height, svg: drownedArt[index] }));

/** Flooded causeway and broken arches share the field's restrained night bed,
 * while telling the road's own story. No additional effect/tween owners. */
export const DROWNED_ROAD = Object.freeze({
  ...FIRST_FIELD,
  id: 'drowned_road',
  layers: FIRST_FIELD.layers.map(layer => layer.kind === 'parallax'
    ? drownedPlanes.find(plane => plane.depth === layer.depth) ?? layer : layer),
}) as AtmospherePreset;

/** Lanternwood — the last warm lamps swallowed by a cold green night.
 * Sparse enough for the intro performance budget: no scanline/post-process
 * layer, one ray, three light-field motes, and four low fog bands. */
export const LANTERNWOOD = forestBiome({
  id: 'lanternwood',
  sky: { from: 0x06110f, to: 0x1a2b27, bands: 6 },
  celestial: { atX: 0.76, atY: 0.15, color: 0xc8d9d2, haloColor: 0x729387, size: 58, haloScale: 3.2, coreAlpha: 0.58, haloAlpha: 0.12 },
  cloudBands: { count: 3, color: 0x50645c, sizeMin: 420, sizeMax: 720, alphaMin: 0.05, alphaMax: 0.12, driftPxPerSec: 4, bandYMin: 0.08, bandYMax: 0.34 },
  motes: { colors: [0xe3a84f, 0x9aad78, 0x667f6e], count: 7, sizeMin: 2, sizeMax: 5, driftPx: 30, periodMinMs: 9000, periodMaxMs: 17000, alphaMin: 0.12, alphaMax: 0.42 },
  rays: { color: 0x8eaa9e, count: 1, widthMin: 80, widthMax: 140, height: 500, tiltDeg: 5, periodMinMs: 15000, periodMaxMs: 24000, alphaMin: 0.03, alphaMax: 0.09 },
  glow: {
    blobs: [
      { color: 0xd58a38, atX: 0.18, atY: 0.72, size: 210, alpha: 0.07 },
      { color: 0xb87430, atX: 0.58, atY: 0.70, size: 180, alpha: 0.05 },
      { color: 0x4f725f, atX: 0.82, atY: 0.86, size: 360, alpha: 0.05 },
    ],
    breathe: { periodMs: 8500, min: 0.78, max: 1.08 },
  },
  lightField: { palette: [0xd58a38, 0x789882], count: 3, sizeMin: 70, sizeMax: 150, alphaMin: 0.02, alphaMax: 0.06, driftRadius: 38, periodMinMs: 14000, periodMaxMs: 23000 },
  weather: { mode: 'fall', color: 0x82958c, colorEnd: 0x42564c, size: 3, frequencyMs: 720, lifespanMs: 3200, speed: 14, gravityY: 4, windX: 24, alphaEnd: 0.22, gust: { periodMs: 11000, min: 0.5, max: 1.2, phase: 0.25 } },
  flicker: {
    color: 0xe09a42,
    size: 150,
    baseAlpha: 0.055,
    flickerAmp: 0.035,
    hzMin: 1.4,
    hzMax: 3.2,
    anchors: [
      { atX: 0.16, atY: 0.70 },
      { atX: 0.38, atY: 0.76 },
      { atX: 0.62, atY: 0.71 },
      { atX: 0.84, atY: 0.78 },
    ],
  },
  parallax: [
    { svg: JOURNEY_DEPTH_BACKDROPS.lanternwood, shape: 'dunes', color: 0x253b32, speed: 0.04, height: JOURNEY_DEPTH_HEIGHTS.lanternwood, alpha: 0.48, detail: 0.55, sway: { ampPx: 6, periodMs: 36000 } },
    { svg: WOODLAND_BACKDROPS.lanternwood.mid, shape: 'spires', color: 0x172b25, speed: 0.10, height: 420, alpha: 0.82, detail: 0.85 },
    { svg: WOODLAND_BACKDROPS.lanternwood.near, shape: 'ridge', color: 0x0d1c18, speed: 0.18, height: 260, alpha: 0.94, detail: 0.9 },
  ],
  fog: { color: 0x6e837a, bands: 4, alphaMin: 0.06, alphaMax: 0.13, driftPx: 40, periodMinMs: 13000, periodMaxMs: 23000, yMin: 0.58, yMax: 0.91, sizeMin: 220, sizeMax: 460 },
  closer: {
    vignette: { color: 0x020806, edgeAlpha: 0.38, washColor: 0x0e211a, washAlpha: 0.05 },
    grade: { top: 0x365348, bottom: 0x10261e, strength: 0.16, bands: 4, blend: 'normal' },
  },
});
